import React, { useEffect, useRef } from 'react';

// Loads GeoGebra's deployggb.js exactly once and resolves when window.GGBApplet
// is available. Subsequent callers reuse the same promise.
let deployGgbPromise = null;
function loadDeployGgb() {
  if (typeof window !== 'undefined' && window.GGBApplet) return Promise.resolve();
  if (deployGgbPromise) return deployGgbPromise;
  deployGgbPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://www.geogebra.org/apps/deployggb.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { deployGgbPromise = null; reject(new Error('Failed to load GeoGebra')); };
    document.head.appendChild(script);
  });
  return deployGgbPromise;
}

// Extracts a GeoGebra material id from the various URL shapes admins paste.
// Returns null for things that can't be embedded as an applet (e.g. GeoGebra
// Classroom links), so callers can fall back to a plain link.
export function getGeoGebraMaterialId(url) {
  if (!url) return null;
  const trimmed = String(url).trim();

  // A bare material id (no slashes/spaces) — accept as-is.
  if (/^[A-Za-z0-9]{4,}$/.test(trimmed)) return trimmed;

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch (e) {
    return null;
  }

  const host = parsed.hostname.replace(/^www\./i, '');
  if (!/(^|\.)geogebra\.org$/i.test(host)) return null;

  // GeoGebra Classroom sessions are live activities, not embeddable applets.
  if (/\/classroom(\/|$)/i.test(parsed.pathname)) return null;

  const patterns = [
    /\/m\/([^/?#]+)/,                                // /m/<id>
    /\/material\/(?:show|iframe)\/id\/([^/?#]+)/,    // /material/show/id/<id>
    /\/(?:classic|graphing|geometry|calculator|cas|scientific|suite|notes|3d)\/([^/?#]+)/, // app URLs
  ];
  for (const re of patterns) {
    const match = parsed.pathname.match(re);
    if (match && match[1]) return match[1];
  }

  const queryId = parsed.searchParams.get('material_id') || parsed.searchParams.get('id');
  return queryId || null;
}

let appletCounter = 0;

// Renders an interactive GeoGebra applet for the given material id using the
// official GGBApplet API. The applet is scaled to fill its container's width
// (up to maxWidth) so the whole construction fits without clipping, instead of
// being pinned to a fixed size that cuts off side panels.
const SCALER_CLASS = 'ggb-applet-scaler';

export default function GeoGebraApplet({ materialId, maxWidth = 1000 }) {
  const idRef = useRef(`ggb-applet-${++appletCounter}`);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!materialId) return undefined;
    let cancelled = false;
    const container = containerRef.current;

    loadDeployGgb()
      .then(() => {
        if (cancelled || !container) return;
        const applet = new window.GGBApplet(
          {
            material_id: materialId,
            // Scale to fit the wrapper width (preserves the full layout, no clipping).
            scaleContainerClass: SCALER_CLASS,
            allowUpscale: true,
            autoHeight: true,
            showToolBar: false,
            showMenuBar: false,
            showAlgebraInput: false,
            showResetIcon: true,
            showFullscreenButton: true,
            enableShiftDragZoom: true,
            borderColor: '#e0e0e0',
          },
          true
        );
        applet.inject(idRef.current);
      })
      .catch(() => { /* network/CSP issue — the external-link fallback still works */ });

    return () => {
      cancelled = true;
      // Tear down the injected applet so re-opening doesn't stack iframes.
      if (container) container.innerHTML = '';
    };
  }, [materialId]);

  return (
    <div className={SCALER_CLASS} style={{ width: '100%', maxWidth, margin: '0 auto' }}>
      <div id={idRef.current} ref={containerRef} />
    </div>
  );
}
